import PropTypes from 'prop-types';
// @mui
import { Box, Tooltip } from '@mui/material';
// hooks
import useLocales from '../../../hooks/useLocales';
// guards
import RoleBasedGuard from '../../../guards/RoleBasedGuard';
//
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';

// ----------------------------------------------------------------------

NavItem.propTypes = {
  active: PropTypes.bool,
  open: PropTypes.bool,
  isCollapse: PropTypes.bool,
  depth: PropTypes.number,
  item: PropTypes.shape({
    children: PropTypes.array,
    icon: PropTypes.any,
    info: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
    disabled: PropTypes.bool,
    lockReason: PropTypes.string,
    caption: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    permission: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default function NavItem({ item, depth, active, open, isCollapse, ...other }) {
  const { translate } = useLocales();

  const {
    title, icon, info, children, disabled, caption, roles, allowed, slug,
    // Two shapes, and both have to reach the guard. `permission` is one code
    // the user must hold; `permissions` is any-of, which is what an item two
    // different jobs can reach needs. checkNavAccess already resolves both,
    // so dropping the array here was silently falling back to `allowed`.
    permission: itemPermission, permissions: itemPermissions, tourId,
    // Set on a paid module shown to a free-wedge org: one line saying what it
    // is and that it is on the paid plan. Its presence is what makes the item
    // inert, here and in NavList.
    lockReason,
  } = item;

  const isLocked = Boolean(lockReason);

  const renderContent = (
    <ListItemStyle
      depth={depth}
      active={active}
      isCollapse={isCollapse}
      disabled={disabled || isLocked}
      {...(isLocked && { 'aria-disabled': true })}
      {...(tourId ? { 'data-tour': tourId } : {})}
      {...other}
    >
      {/* {icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}

      
      {depth !== 1 && <DotIcon active={active && depth !== 1} />} */}

      {icon ? <ListItemIconStyle>{icon}</ListItemIconStyle> : depth !== 1 && <DotIcon active={active} />}

      <ListItemTextStyle
        isCollapse={isCollapse}
        primary={translate(title)}
        secondary={
          caption && (
            <Tooltip title={translate(caption)} placement="top-start">
              <span>{translate(caption)}</span>
            </Tooltip>
          )
        }
        primaryTypographyProps={{
          noWrap: true,
          variant: active ? 'subtitle2' : 'body2',
        }}
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'caption',
        }}
      />

      {!isCollapse && (
        <>
          {info && (
            <Box component="span" sx={{ lineHeight: 0 }}>
              {info}
            </Box>
          )}

          {!!children && (
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              sx={{
                width: 16,
                height: 16,
                ml: 1,
                flexShrink: 0,
                color: 'text.disabled',
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
                ...(open && { transform: 'rotate(90deg)' }),
              }}
            />
          )}
        </>
      )}
    </ListItemStyle>
  );

  return (
    <RoleBasedGuard
      roles={roles}
      allowed={allowed}
      slug={slug}
      permission={itemPermission}
      permissions={itemPermissions}
      isNav
    >
      {/* In the rail the icon is all there is, so the name has to come from
          somewhere. Hovering is that somewhere. */}
      {isLocked && (
        <Tooltip title={lockReason} placement="right" arrow>
          {/* A disabled ListItem swallows pointer events, so the tooltip needs
              a wrapper that still receives them. Without it the reason never
              shows and the item is a dead row with no explanation. */}
          <span style={{ display: 'block' }}>{renderContent}</span>
        </Tooltip>
      )}
      {!isLocked && isCollapse && (
        <Tooltip title={translate(title)} placement="right" arrow>
          {renderContent}
        </Tooltip>
      )}
      {!isLocked && !isCollapse && renderContent}
    </RoleBasedGuard>
  );
}

// ----------------------------------------------------------------------

DotIcon.propTypes = {
  active: PropTypes.bool,
};

export function DotIcon({ active }) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}
